import { modelHelperModule } from './model-helper';
import { appConfig } from '../constants/app-config';
import { defineValue } from '../utils/object-utils/object-utils';

export const teamCandidateModelModule = angular
  .module('Q.models.team-candidate', [modelHelperModule])

  .factory('TeamCandidate', function (ModelHelper, $injector, $http) {
    const STATE_ORDER = {
      idle: 5,
      invited: 0,
      started: 1,
      submitted: 2,
      risk_detected: 2,
      reviewed: 3,
      approved: 4,
      rejected: 4,
      archived: 4,
    };

    let liveInterviewService;

    function clearLiveinterviewData(tc) {
      // clear any old live interview data
      if (tc && tc.assessmentResults && tc.assessmentResults.length) {
        if (!liveInterviewService) {
          // avoid hard-requirements against the service
          liveInterviewService =
            $injector.has('liveInterviewService') &&
            $injector.get('liveInterviewService');
        }
        if (liveInterviewService) {
          tc.assessmentResults.forEach(liveInterviewService.destroy);
        }
      }
    }

    const TeamCandidate = ModelHelper.defineResource({
      name: 'TeamCandidate',
      endpoint: 'team_candidates',

      computed: {
        nameOrEmail: /* @ngInject */ function (name, email, forgotten) {
          return (
            name ||
            email ||
            (forgotten ? '(Contact Info Removed)' : '(No Contact Information)')
          );
        },
        hasNameOrEmail: /* @ngInject */ function (name, email) {
          return !!(name || email);
        },
      },

      staticMethods: {
        // shared labels between TeamCandidate and AssessmentResult
        stateNameMappings: {
          idle: 'Idle',
          invited: 'Invited',
          opened: 'Opened',
          started: 'Started',
          submitting: 'Submitting',
          submitted: 'Submitted',
          risk_detected: 'Plagiarism Risk',
          reviewed: 'Reviewed',
          'submitted-candidateSubmitted': 'Submitted by candidate',
          'submitted-timerRanOut': 'Autosubmitted after timer ran out',
          'submitted-teamDecided': 'Closed by team decision',
          'submitted-teamForced': 'Closed by team member',
          'submitted-invitationExpired': 'Closed by invitation expiration',
          'submitted-assessmentArchived': 'Closed by assessment archival',
          approved: 'Approved',
          rejected: 'Rejected',
          archived: 'Archived',
          terminated: 'Closed without submitting',
        },
        /**
         *
         * @param data {Object}
         * @param data.bulkAction {String} Action to carry out
         * @param data.ids {Array<String>} Array of ids
         * @param data.data {Object} additional data to pass along
         * @param config {Object} $http config
         * @returns {HttpPromise}
         */
        bulkUpdate(data, config) {
          return $http.post(
            `${appConfig.apiRoot}team_candidates/bulk_update/`,
            data,
            config,
          );
        },
      },

      methods: {
        openAssessmentCount() {
          return (this.assessmentResults || []).filter(
            (ar) => ar.state === 'opened' || ar.state === 'started',
          ).length;
        },
        invitedAssessmentCount() {
          return (this.assessmentResults || []).filter(
            (ar) => ar.state === 'invited',
          ).length;
        },
        isIdle() {
          return this.state === 'idle';
        },
        isInvited() {
          return this.state === 'invited';
        },
        isStarted() {
          return this.state === 'started';
        },
        isSubmitted() {
          return this.state === 'submitted';
        },
        isSubmittedOrRisky() {
          return this.state === 'submitted' || this.state === 'risk_detected';
        },
        isReviewed() {
          return this.state === 'reviewed';
        },
        isDecided() {
          return (
            ['approved', 'rejected', 'archived'].indexOf(this.state) !== -1
          );
        },
        isUndecided() {
          return this.testTeamMember || !this.isDecided();
        },
        isArchived() {
          return this.state === 'archived';
        },
        isApproved() {
          return this.state === 'approved';
        },
        isRejected() {
          return this.state === 'rejected';
        },
        canDecide() {
          return (
            ['started', 'submitted', 'risk_detected', 'reviewed'].indexOf(
              this.state,
            ) !== -1
          );
        },
        canRestore() {
          return (
            this.isDecided() && this.prevState && this.prevState !== 'archived'
          );
        },
        canBeArchived() {
          return !this.isArchived();
        },
        stateName(value, includeArchived) {
          let val = TeamCandidate.stateNameMappings[value || this.state];
          let prevState = TeamCandidate.stateNameMappings[this.prevState];
          if (
            includeArchived &&
            this.isArchived() &&
            this.prevState !== 'archived' &&
            prevState
          ) {
            val += ', was ' + prevState;
          }
          return val;
        },
        hasState(state) {
          return STATE_ORDER[state] <= (STATE_ORDER[this.state] || 0);
        },
        toString() {
          return this.nameOrEmail;
        },
        toSelection() {
          const tc = this;
          if (!tc.$selection) {
            const sel = { id: tc.id };
            defineValue(sel, 'reload', () => {
              tc.toSelection();
            });
            defineValue(tc, '$selection', sel);
          }
          const sel = tc.$selection;
          sel.name = tc.nameOrEmail;
          sel.email = tc.email; // for inviting
          sel.stateName = tc.stateName(null, true);
          sel.flags = [
            tc.canBeArchived() && 'archive',
            tc.canRestore() && 'restore',
            tc.canDecide() && 'decide',
            tc.invitedAssessmentCount() && 'invited',
            tc.openAssessmentCount() && 'open',
          ].filter(angular.identity);
          return sel;
        },
      },
      relations: {
        hasMany: {
          Assessment: {
            localField: 'assessments',
            localKeys: 'assessmentIds',
          },
          AssessmentCohort: {
            localField: 'cohorts',
            localKeys: 'cohortIds',
          },
          AssessmentResult: {
            localField: 'assessmentResults',
            foreignKey: 'teamCandidateId',
            get: function (Resource, relationDef, instance, origGetter) {
              return origGetter().filter((ar) => ar.currentTake);
            },
          },
          Solution: {
            localField: 'solutions',
            foreignKey: 'teamCandidateId',
          },
          Note: {
            localField: 'notes',
            foreignKey: 'notableId',
          },
          TeamMember: {
            localField: 'assignedTeamMembers',
            localKeys: 'assignedTeamMemberIds',
          },
          AssessmentResultReview: {
            localField: 'assessmentResultReviews',
            foreignKey: 'teamCandidateId',
          },
          SolutionReview: {
            localField: 'solutionReviews',
            foreignKey: 'teamCandidateId',
          },
          AssessmentInvitation: {
            localField: 'assessmentInvitations',
            foreignKey: 'teamCandidateId',
          },
        },
        hasOne: {
          Candidate: {
            localField: 'candidate',
            localKey: 'candidateId',
          },
          Team: {
            localField: 'team',
            localKey: 'teamId',
          },
        },
      },

      meta: {
        includeOnPull: ['digest', 'signals_profile'],
        hasDigest: true,
        actionEndpoints: {
          approve: {
            method: 'PUT',
            track: true,
            onSuccess: clearLiveinterviewData,
          },
          cancel: {
            method: 'PUT',
            track: true,
            onSuccess: clearLiveinterviewData,
          },
          reject: {
            method: 'PUT',
            track: true,
            onSuccess: clearLiveinterviewData,
          },
          restore: {
            method: 'PUT',
            track: true,
            onSuccess: clearLiveinterviewData,
          },
          close: {
            method: 'PUT',
            track: true,
            onSuccess: clearLiveinterviewData,
          },
          archive: {
            method: 'PUT',
            track: true,
            onSuccess: clearLiveinterviewData,
          },
          assign: { method: 'PUT', track: true },
          forget: { method: 'PUT', track: true },
        },
      },
    });

    return TeamCandidate;
  });
